import type { EmailVerificationInput } from '../../generated/graphql-viewer';

import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import { useGetProtectionTokenLazyQuery } from '../../generated/graphql-viewer';

type AccessContextValue = {
  email?: string;
  accessCode?: string;
  protectionToken?: string;
  error?: string;
  setEmailVerificationData?: (emailVerificationData: EmailVerificationInput) => void;
  setError?: (error: string) => void;
};

const initialContext: AccessContextValue = {};

export const AccessContext = createContext<AccessContextValue>(initialContext);

type Props = {
  email?: string;
  accessCode?: string;
  children: ReactNode;
};

export function ViewerAccessProvider({ email, accessCode, children }: Props) {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const hmacToken = searchParams.get('hmac-token') || '';

  const [emailVerificationData, setEmailVerificationData] = useState<EmailVerificationInput | undefined>();
  const [error, setError] = useState<string>();

  const [getProtectionToken, getProtectionTokenResponse] = useGetProtectionTokenLazyQuery();

  const protectionToken =
    getProtectionTokenResponse?.data?.protectionTokenFor?.__typename === 'ProtectionTokenSuccess'
      ? getProtectionTokenResponse.data.protectionTokenFor.token
      : undefined;

  useEffect(() => {
    if (hmacToken) getProtectionToken({ variables: { webcastId, protectionTokenInput: { hmacToken } } });

    if (emailVerificationData)
      getProtectionToken({
        variables: { webcastId, protectionTokenInput: { emailVerification: emailVerificationData } },
        onCompleted: (data) => {
          if (data.protectionTokenFor?.__typename === 'ProtectionTokenProblem') {
            if (data.protectionTokenFor.code === 'INVALID_EMAIL_VERIFICATION_CODE')
              setError(t('viewer.views.accessForm.emailVerificationCode.invalidCodeError'));
          }
        },
      });
  }, [emailVerificationData, getProtectionToken, hmacToken, t, webcastId]);

  return (
    <AccessContext.Provider value={{ email, accessCode, protectionToken, error, setEmailVerificationData, setError }}>
      {children}
    </AccessContext.Provider>
  );
}

export const useViewerAccess = () => useContext(AccessContext);
